import { useEffect, useMemo, useState } from 'react';

import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { addToast } from '@/app/slices/toast';
import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';

import { useFreezeContext } from '../hooks/useFreezeContext';

import './FreezeQC.css';

type FreezeQCProps = {
  className?: string;
};

export const FreezeQC = ({ className }: FreezeQCProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { qcForm, setQcForm } = useFreezeContext();
  const [localQcForm, setLocalQcForm] = useState(qcForm);

  const hasUnsavedChanges = useMemo(() => {
    return JSON.stringify(localQcForm) !== JSON.stringify(qcForm);
  }, [localQcForm, qcForm]);

  const isSaved = useMemo(() => {
    return !hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  useEffect(() => {
    setLocalQcForm(qcForm);
  }, [qcForm]);

  const handleSave = () => {
    setQcForm(localQcForm);
    dispatch(
      addToast({
        severity: 'success',
        summary: t('common.success'),
        detail: t('qc.qcSettingsSavedSuccessfully'),
        sticky: false,
      }),
    );
  };

  return (
    <div className={`freeze-qc-form-container ${className || ''}`}>
      <div className="label-input-group">
        <label>{t('qc.egLotNumber')}</label>
        <InputText
          value={localQcForm.qcEg10 || ''}
          onChange={(e) => {
            setLocalQcForm({ ...localQcForm, qcEg10: e.target.value });
          }}
        />
      </div>

      <div className="label-input-group">
        <label>{t('qc.egExpirationDate')}</label>
        <Calendar
          value={
            localQcForm.qcEg10Date ? new Date(localQcForm.qcEg10Date) : null
          }
          onChange={(e) => {
            setLocalQcForm({ ...localQcForm, qcEg10Date: e.value });
          }}
          showIcon
        />
      </div>

      <div className="label-input-group">
        <label>{t('qc.trypsinExpirationDate')}</label>
        <Calendar
          value={
            localQcForm.qcTryPsInDate
              ? new Date(localQcForm.qcTryPsInDate)
              : null
          }
          onChange={(e) => {
            setLocalQcForm({ ...localQcForm, qcTryPsInDate: e.value });
          }}
          showIcon
        />
      </div>

      <div className="label-input-group">
        <label>{t('qc.ehmExpirationDate')}</label>
        <Calendar
          value={localQcForm.qcehmDate ? new Date(localQcForm.qcehmDate) : null}
          onChange={(e) => {
            setLocalQcForm({ ...localQcForm, qcehmDate: e.value });
          }}
          showIcon
        />
      </div>

      <div className="label-input-group">
        <label>{t('qc.sucroseExpirationDate')}</label>
        <Calendar
          value={
            localQcForm.qcSucroseDate
              ? new Date(localQcForm.qcSucroseDate)
              : null
          }
          onChange={(e) => {
            setLocalQcForm({ ...localQcForm, qcSucroseDate: e.value });
          }}
          showIcon
        />
      </div>

      <div className="label-input-group">
        <label>{t('freeze.freezingComment')}</label>
        <InputText
          value={localQcForm.qcfreezingComment || ''}
          onChange={(e) => {
            setLocalQcForm({
              ...localQcForm,
              qcfreezingComment: e.target.value,
            });
          }}
        />
      </div>

      <div className="label-input-group">
        <label>{t('qc.vsExpirationDate')}</label>
        <Calendar
          value={
            localQcForm.vitrificationSolutionDate
              ? new Date(localQcForm.vitrificationSolutionDate)
              : null
          }
          onChange={(e) => {
            setLocalQcForm({
              ...localQcForm,
              vitrificationSolutionDate: e.value,
            });
          }}
          showIcon
        />
      </div>

      <div className="label-input-group">
        <label>{t('qc.esExpirationDate')}</label>
        <Calendar
          value={
            localQcForm.equilibrationSolutionDate
              ? new Date(localQcForm.equilibrationSolutionDate)
              : null
          }
          onChange={(e) => {
            setLocalQcForm({
              ...localQcForm,
              equilibrationSolutionDate: e.value,
            });
          }}
          showIcon
        />
      </div>

      <div className="label-input-group">
        <label>{t('straw.strawLotNumber')}</label>
        <InputText
          value={localQcForm.qcStrawLotNumber || ''}
          onChange={(e) => {
            setLocalQcForm({
              ...localQcForm,
              qcStrawLotNumber: e.target.value,
            });
          }}
        />
      </div>

      <div className="label-input-group">
        <label>{t('straw.vitStrawLotNumber')}</label>
        <InputText
          value={localQcForm.qcVitStrawLotNumber || ''}
          onChange={(e) => {
            setLocalQcForm({
              ...localQcForm,
              qcVitStrawLotNumber: e.target.value,
            });
          }}
        />
      </div>

      <div className="label-input-group">
        <label>{t('straw.sterileStrawLotNumber')}</label>
        <InputText
          value={localQcForm.qcSterileStrawLotNumber || ''}
          onChange={(e) => {
            setLocalQcForm({
              ...localQcForm,
              qcSterileStrawLotNumber: e.target.value,
            });
          }}
        />
      </div>

      <div className="label-input-group">
        <label>{t('qc.twelveWellLotNumber')}</label>
        <InputText
          value={localQcForm.qc12WellPlates || ''}
          onChange={(e) => {
            setLocalQcForm({ ...localQcForm, qc12WellPlates: e.target.value });
          }}
        />
      </div>

      <div className="label-input-group">
        <label htmlFor="save-button">&nbsp;</label>
        <Button
          id="save-button"
          icon={isSaved ? 'pi pi-check' : 'pi pi-save'}
          onClick={handleSave}
          tooltip={isSaved ? t('common.changesSaved') : t('common.saveChanges')}
          tooltipOptions={{ position: 'top' }}
          severity={isSaved ? 'success' : 'secondary'}
          disabled={!hasUnsavedChanges}
        />
      </div>
    </div>
  );
};
